import { FC, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Grid } from '@mui/material';

import { AddButton } from 'components/_basic/Form/FieldArray/components/AddButton';
import { RemoveButton } from 'components/_basic/Form/FieldArray/components/RemoveButton';
import { NumberInputControlled } from 'components/_basic/Form/NumberInput';
import { TextInputControl } from 'components/_basic/Form/TextInput';
import { FocusedFieldAttribute } from 'components/BusinessToolForm/Instructions/InstructionsByAttribute/types';
import { reCalculatedPeriodValues } from 'components/BusinessToolForm/utils/helpers';

import { concatWithDot } from 'helpers/string';

import { IFieldTableSubRowProps } from './types';
import { NAME_FIELD_NAME } from '../../../../utils/form';

import styles from './FieldTableSubRow.module.scss';

const defaultGetRowName = (name: string, columnName: string, rowIndex?: number) => {
  return concatWithDot(name, rowIndex?.toString() || '', columnName);
};

export const FieldTableSubRow: FC<IFieldTableSubRowProps> = ({
  name,
  rowIndex,
  columnsNames,
  disabled = false,
  firstColumnWidth,
  canAddMore,
  canRemove,
  totalRowCount,
  handleAddSubRow,
  handleRemoveSubRow,
  getRowName = defaultGetRowName,
  handleUpdateSubRow,
}) => {
  const [isNeedToRecalculate, setIsNeedToRecalculate] = useState(false);
  const { control, getValues } = useFormContext();

  const { t } = useTranslation();

  const firstFieldName = getRowName(name, NAME_FIELD_NAME, rowIndex);

  const isLastSubRow = totalRowCount - 1 === rowIndex;

  const onBlurHandler = async (fieldName: string) => {
    if (!isNeedToRecalculate) return;

    const values = getValues(name);
    const updatedPeriodValues = reCalculatedPeriodValues(fieldName, values[rowIndex]);
    handleUpdateSubRow(rowIndex, updatedPeriodValues);
    setIsNeedToRecalculate(false);
  };

  return (
    <Grid container className={styles.container} wrap="nowrap" gap={1}>
      <Grid item sx={{ width: firstColumnWidth, minWidth: firstColumnWidth }} className={styles.titleRowContainer}>
        <Grid container gap={1} columns={2} wrap="nowrap" className={styles.titleWrapper}>
          <Grid item className={styles.removeButtonContainer}>
            <RemoveButton
              canRemove={canRemove}
              notRequired={false}
              disabled={disabled}
              onClick={() => handleRemoveSubRow(rowIndex)}
              buttonStyles={styles.removeButton}
              containerStyles={styles.removeButtonContainerStyles}
            />
          </Grid>
          <Grid item>
            <TextInputControl
              control={control}
              id={firstFieldName}
              name={firstFieldName}
              disabled={disabled}
              fieldClassName={styles.textInput}
              sx={{ minWidth: 110 }}
            />
          </Grid>
        </Grid>
        {isLastSubRow && (
          <Grid className={styles.addMoreButtonContainer} item xs>
            <AddButton
              canAddMore={canAddMore}
              onClick={handleAddSubRow}
              disabled={disabled}
              variant="text"
              className={styles.addMoreButton}
            >
              {t('addNew')}
            </AddButton>
          </Grid>
        )}
      </Grid>
      <Grid item xs>
        <Grid container gap={1} columns={columnsNames.length} wrap="nowrap">
          {columnsNames.map((columnName) => {
            const fieldName = getRowName(name, columnName, rowIndex);
            return (
              <Grid key={columnName} item xs={1}>
                <NumberInputControlled
                  control={control}
                  id={fieldName}
                  name={fieldName}
                  disabled={disabled}
                  fieldClassName={styles.tableCell}
                  onBlur={() => onBlurHandler(fieldName)}
                  onChange={() => setIsNeedToRecalculate(true)}
                  data-mirror-field={FocusedFieldAttribute.fundingDirectionsTable}
                  allowNegative
                />
              </Grid>
            );
          })}
        </Grid>
      </Grid>
    </Grid>
  );
};
